<script setup lang="ts">
import PdsAuthShell from '@/components/pds/PdsAuthShell.vue';
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsField from '@/components/pds/PdsField.vue';
import PdsHeading from '@/components/pds/PdsHeading.vue';
import { authService } from '@/stores/auth/auth.service';
import { useAuthStore } from '@/stores/auth/auth.slice';
import type {
  GetMyDataResponseDto,
  UpdateSecretPayloadDto,
} from '@/types/request.dto';
import { uuidv4 } from '@/utils/uuid';
import { useToast } from 'primevue';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const prescript = reactive({ emailLoaded: false });

const authStore = useAuthStore();

const route = useRoute();
const toast = useToast();

const token = computed(() => {
  return route.query.token instanceof Array
    ? route.query.token[0]
    : route.query.token;
});
const prevEmail = ref<string | null>();
const isLoading = ref(false);
const pageTab = ref<
  | 'loading'
  | 'email_form'
  | 'link_sent'
  | 'new_password_form'
  | 'password_updated'
>('loading');

const tempJWT = ref<string>(null);
const email = ref('');
const psw = ref('');
const cpsw = ref('');
const pswRejected = ref(false);

const checkTab = async (token: string, prevEmail: string) => {
  if (!prescript.emailLoaded) return;

  if (
    (!token || !prevEmail) &&
    !['email_form', 'link_sent'].includes(pageTab.value)
  )
    return (pageTab.value = 'email_form');

  if (
    tempJWT.value &&
    !['new_password_form', 'password_updated'].includes(pageTab.value)
  )
    return (pageTab.value = 'new_password_form');
  if (tempJWT.value) return;

  try {
    const requestId = uuidv4();
    const abortCrt = new AbortController();
    const login = await authService.login(
      { password: token, emailOrPhone: prevEmail },
      { requestId, signal: abortCrt.signal },
    );
    if (login?.data) {
      tempJWT.value = login.data?.tokens?.accessToken;
      pageTab.value = 'new_password_form';
    } else pageTab.value = 'email_form';
  } catch (error) {
    pageTab.value = 'email_form';
  }
};

const submitRequestOTP = async () => {
  isLoading.value = true;
  const res = await authStore
    .sendResetOTP({ email: email.value })
    .catch(() => ({ data: false }))
    .finally(() => (isLoading.value = false));

  if (res.data) {
    localStorage.setItem('request_email', email.value);
    pageTab.value = 'link_sent';
  } else toast.add({ summary: 'E-mail non enregistre', severity: 'info' });
};

const submitUpdateSecret = async () => {
  if (psw.value.length < 8 || psw.value !== cpsw.value) {
    pswRejected.value = true;
    return;
  }

  const payload: UpdateSecretPayloadDto = { secret: psw.value };
  const requestId = uuidv4();
  const abortCrt = new AbortController();

  isLoading.value = true;
  const mydata = await authService
    .updateSecret(payload, {
      requestId,
      signal: abortCrt.signal,
      jwttoken: tempJWT.value,
    })
    .catch(() => null as GetMyDataResponseDto)
    .finally(() => (isLoading.value = false));
  if (!mydata) {
    return toast.add({
      summary: 'Un probléme est survenu, veuillez réessayer',
    });
  }
  pageTab.value = 'password_updated';
};

watch(
  [token, prevEmail],
  ([token, prevEmail]) => {
    checkTab(token, prevEmail);
  },
  { immediate: true },
);

onMounted(() => {
  prevEmail.value = localStorage.getItem('request_email') || null;
  prescript.emailLoaded = true;
});
</script>

<template>
  <PdsAuthShell>
    <form
      v-if="pageTab === 'email_form'"
      class="flex w-full flex-col gap-5"
      @submit.prevent="submitRequestOTP"
    >
      <PdsHeading title="Mot de passe oublié">
        Un lien de réinitialisation part vers l’adresse du compte.
      </PdsHeading>
      <PdsField
        id="recove-email"
        v-model="email"
        type="email"
        label="Adresse du commerce"
        autocomplete="email"
        placeholder="nom@commerce.be"
        required
      />
      <div class="flex w-full gap-4">
        <PdsButton variant="second" to="/login">Retour</PdsButton>
        <PdsButton type="submit" :loading="isLoading">
          Envoyer le lien
        </PdsButton>
      </div>
    </form>

    <template v-else-if="pageTab === 'link_sent'">
      <PdsHeading title="Lien envoyé">
        Vérifiez la boîte de réception de l’adresse du compte, puis suivez le
        lien.
      </PdsHeading>
      <div class="flex w-full gap-4">
        <PdsButton variant="second" to="/login">Retour</PdsButton>
        <PdsButton @click="pageTab = 'email_form'">
          Modifier l’adresse
        </PdsButton>
      </div>
    </template>

    <form
      v-else-if="pageTab === 'new_password_form'"
      class="flex w-full flex-col gap-5"
      @submit.prevent="submitUpdateSecret"
    >
      <PdsHeading title="Nouveau mot de passe" :alert="pswRejected">
        <template v-if="pswRejected">
          Les deux mots de passe doivent être identiques, 8 caractères minimum.
        </template>
        <template v-else>
          Choisissez un mot de passe d’au moins 8 caractères.
        </template>
      </PdsHeading>
      <div class="flex w-full flex-col gap-4">
        <PdsField
          id="recove-psw"
          v-model="psw"
          type="password"
          label="Nouveau mot de passe"
          autocomplete="new-password"
          required
          :invalid="pswRejected"
          @update:model-value="pswRejected = false"
        />
        <PdsField
          id="recove-cpsw"
          v-model="cpsw"
          type="password"
          label="Confirmer le mot de passe"
          autocomplete="new-password"
          required
          :invalid="pswRejected"
          @update:model-value="pswRejected = false"
        />
      </div>
      <PdsButton type="submit" :loading="isLoading">Valider</PdsButton>
    </form>

    <template v-else-if="pageTab === 'password_updated'">
      <PdsHeading title="Mot de passe mis à jour">
        Vous pouvez vous connecter avec votre nouveau mot de passe.
      </PdsHeading>
      <PdsButton to="/login">Se connecter</PdsButton>
    </template>
  </PdsAuthShell>
</template>
